import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Users, Search, User, Shield, Building2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, companiesRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/companies`),
      ]);
      setUsers(usersRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find((c) => c.id === companyId);
    return company?.name || "—";
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-active`);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, is_active: !currentStatus } : u
        )
      );
      toast.success(currentStatus ? "Utilisateur désactivé" : "Utilisateur activé");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const filteredUsers = users.filter((user) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      user.first_name.toLowerCase().includes(searchLower) ||
      user.last_name.toLowerCase().includes(searchLower)
    );
  });

  const roleLabels = {
    admin: { label: "Admin", color: "bg-red-100 text-red-700" },
    company: { label: "Manager", color: "bg-orange-100 text-orange-700" },
    technician: { label: "Technicien", color: "bg-sky-100 text-sky-700" },
    observer: { label: "Observateur", color: "bg-slate-100 text-slate-600" },
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="admin-users">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/admin")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold text-slate-900">Utilisateurs</h1>
            </div>
            <span className="text-sm text-slate-500">
              {users.length} utilisateur(s)
            </span>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Rechercher un utilisateur..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-12 pl-10"
              data-testid="search-input"
            />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 bg-white rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredUsers.length > 0 ? (
          <div className="space-y-3">
            {filteredUsers.map((user) => {
              const roleConfig = roleLabels[user.role] || roleLabels.observer;
              return (
                <Card key={user.id} data-testid={`user-card-${user.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            user.is_active ? "bg-sky-100" : "bg-slate-100"
                          }`}
                        >
                          {user.role === "admin" ? (
                            <Shield
                              className={`w-6 h-6 ${
                                user.is_active ? "text-red-500" : "text-slate-400"
                              }`}
                            />
                          ) : (
                            <User
                              className={`w-6 h-6 ${
                                user.is_active ? "text-sky-600" : "text-slate-400"
                              }`}
                            />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900">
                              {user.first_name} {user.last_name}
                            </h3>
                            <span
                              className={`px-2 py-0.5 text-xs rounded ${roleConfig.color}`}
                            >
                              {roleConfig.label}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500">{user.email}</p>
                          {user.company_id && (
                            <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                              <Building2 className="w-3 h-3" />
                              {getCompanyName(user.company_id)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p
                            className={`text-sm ${
                              user.is_active ? "text-green-600" : "text-slate-400"
                            }`}
                          >
                            {user.is_active ? "Actif" : "Inactif"}
                          </p>
                        </div>
                        {user.role !== "admin" && (
                          <Switch
                            checked={user.is_active}
                            onCheckedChange={() =>
                              handleToggleActive(user.id, user.is_active)
                            }
                            data-testid={`toggle-user-${user.id}`}
                          />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg mb-2">Aucun utilisateur trouvé</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminUsers;
